import { ColumnDef } from "@tanstack/table-core/build/lib/types";
import { Exception } from "../../graphql/graphql";
import { mapStatusToColor } from "../../shared/Utils";
import { Tag } from "@nike/eds";
import { ExceptionSortField } from "../../graphql/graphql";
import { formatDateTime, getUrgencyTag, formatDate } from "../../shared/Utils";
import { modeOfTransportOptions, statusOptions, isEnumValue, productDivisionOptions } from "../../shared/ColumnOptions";

export type TableColumn<T> = ColumnDef<T, any> & {
  header: string | React.ReactNode | (() => React.ReactNode);
  accessorKey: keyof T | string;
  filterLabel?: string;
  filterType?: "select" | "text" | "dateRange" | "date";
  filterOptions?: Array<{ value: string; label: string }>;
  getFilterOptions?: (tableState: any) => Array<{ value: string; label: string }>;
  enableHiding?: boolean;
  enableSorting?: boolean;
  sortableField?: ExceptionSortField;
  filterKey?: string;
};


export const getTableColumns = (): TableColumn<Exception>[] => [
  {
    header: () => (<span title="Urgency">Urgency</span>),
    accessorKey: "targetResolutionDate_eta",
    size: 120,
    enableHiding: false,
    enableSorting: true,
    //TODO: this property needs to available as a sortable field for exceptions
    sortableField: ExceptionSortField.TargetResolutionDate,
    cell: ({ row }) => {
      const { color, text } = getUrgencyTag(row.original);
      return (
        <Tag color={color} size="small" style={{whiteSpace: "nowrap"}}>
          {text}
        </Tag>
      );
    },
    enableColumnFilter: false,
    filterType: "select",
    filterLabel: "Urgency",
    filterKey: "targetResolutionDate",
    getFilterOptions: (table) => table.options.meta?.urgencyOptions || [],
  },
  {
    header: () => (<span title="ETA">ETA</span>),
    accessorKey: "targetResolutionDate",
    size: 100,
    enableSorting: isEnumValue(ExceptionSortField, ExceptionSortField.TargetResolutionDate),
    sortableField: ExceptionSortField.TargetResolutionDate,
    cell: ({ row }) => formatDate(row.original.targetResolutionDate, false),
    enableColumnFilter: true,
    filterType: "dateRange",
    filterLabel: "ETA",
    filterKey: "targetResolutionDate",
  },
  {
    header: () => (<span title="Mode of Transport">MOT</span>),
    accessorKey: "modeOfTransport",
    accessorFn: (row) => row.consignment.modeOfTransport,
    size: 80,
    enableHiding: false,
    enableSorting: false,
    filterLabel: "Mode of Transport",
    filterType: "select",
    filterOptions: modeOfTransportOptions,
  },
  {
    header: () => (<span title="Exception ID">ID</span>),
    accessorKey: "id",
    size: 100,
    enableHiding: true,
    enableSorting: false,
    enableColumnFilter: false,
      cell: ({ row }) => (
    <span className='ellipsis' title={row.original.alias}>{row.original.alias}</span>
  ),
  },
  {
    header: () => (<span title="Status">Status</span>),
    accessorKey: "status",
    size: 120,
    enableHiding: false,
    enableSorting: false,
    cell: ({ row }) => {
      const statusText = row.original.status.replace(/_/g, " ");
      const statusColor = mapStatusToColor(row.original.status);
      return (
        <Tag
          color={statusColor}
          size="small"
          style={{whiteSpace: "nowrap"}}
        >{statusText}</Tag>
      );
    },
    filterLabel: "Status",
    filterType: "select",
    filterOptions: statusOptions,
  },
  {
    header: () => (<span title="Exception Type">Exception Type</span>),
    accessorKey: "description",
    size: 200,
    filterLabel: "Exception Type",
    filterType: "select",
    getFilterOptions: (table) => table.options.meta?.exceptionTypeOptions || [],
    // The key for this filter as it is different from the accessorKey
    filterKey: "exceptionType",
    enableSorting: false,
  },
  {
    header: () => (<span title="Product Division">Product Division</span>),
    accessorKey: "consignment_productDivisions",
    size: 120,
    enableSorting: false,
    enableColumnFilter: true,
    filterKey: "productDivision",
    filterLabel: "Product Division",
    filterType: "select",
    filterOptions: productDivisionOptions,
    cell: ( {row} ) => {
      const divisions = row.original.consignment.productDivisions;
      if (Array.isArray(divisions) && divisions.length > 0) {
        return (
          <div>
            {divisions.map((div, idx) => (
              <div key={idx}>{div}</div>
            ))}
          </div>
        );
      }
      return <div>Unassigned</div>;
    },
  },
  {
    header: () => (<span title="Bill of Lading Number">BOL</span>),
    accessorKey: "billOfLading",
    accessorFn: (row) => row.consignment.billOfLadingNbr || "Unassigned",
    size: 180,
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    header: () => (<span title="Country of Export">Country of Export</span>),
    accessorKey: "originCountry",
    accessorFn: (row) => row.consignment.originCountry,
    size: 140,
    enableSorting: false,
    filterLabel: "Country of Export",
    filterType: "select",
    getFilterOptions: (table) => table.options.meta?.originCountriesOptions || [],
  },
  {
    header: () => (<span title="Country of Import">Country of Import</span>),
    accessorKey: "countryOfImport",
    accessorFn: (row) => row.consignment.dischargeCountry,
    size: 140,
    enableSorting: false,
    filterLabel: "Country of Import",
    filterType: "select",
    getFilterOptions: (table) => table.options.meta?.importCountriesOptions || [],
  },
  {
    header: () => (<span title="Assigned User">Assigned User</span>),
    accessorKey: "assignee_name",
    accessorFn: (row) => row.assignee?.name || "Unassigned",
    size: 150,
    enableSorting: false,
    enableColumnFilter: true,
    filterLabel: "Assigned User",
    filterType: "select",
    filterKey: "assigneeUserId",
    getFilterOptions: (table) => table.options.meta?.assigneeOptions || [],
  },
  {
    header: () => (<span title="Creation Date">Creation Date</span>),
    accessorKey: "creationDate",
    // accessorFn: (row) => row.creationDate || "Unassigned",
    cell: ({ row }) => formatDateTime(row.original.creationDate, true),
    size: 228,
    sortableField: ExceptionSortField.CreationDate,
    enableSorting: true,
    enableColumnFilter: false,
    enableHiding: true,
  },
];